// FP3-080B — Articulation Screener DOB/Sex preflight gate.
//
// Blocks providers from starting an Articulation Screener when the
// selected student is missing a parseable Date of Birth or a norm-
// eligible Sex value. FP3-080 moved articulation norm selection to
// live off `student.sex`, so completing an entire screener without
// these fields would leave the results uninterpretable. This modal
// captures only what's missing, PATCHes the student, then hands
// control back to the runner so the screener starts immediately.
//
// Guardrails: never silently defaults Sex, never silently skips DOB,
// never reintroduces the removed `speech_norm_category` field, and
// applies only to the Articulation Screener path.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

// Sex normalization mirrors backend `normalize_sex_for_norms`
// (/app/backend/lib/articulation_norm_engine.py). Male/M → male norms,
// Female/F → female norms; every other value (blank, other,
// not_specified, unknown token) fails the preflight and is prompted.
export function isSexValidForArticulationNorms(value) {
  const v = (value || "").trim().toLowerCase();
  return v === "male" || v === "m" || v === "female" || v === "f";
}

// DOB must be present AND parseable to a real calendar date. Accepts
// the ISO YYYY-MM-DD shape that the student demographic PATCH stores,
// but also tolerates whatever legacy shape may live on old docs — as
// long as `new Date()` gives us a real day, we treat it as valid.
export function isDobValidForArticulationNorms(value) {
  const raw = (value || "").trim();
  if (!raw) return false;
  const d = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw);
  if (Number.isNaN(d.getTime())) return false;
  // Guard against future-dated DOBs — norming assumes a real past
  // birthday. A silently-future DOB would mint negative age months
  // and still bypass this gate otherwise.
  const now = new Date();
  return d.getTime() <= now.getTime();
}

export function articulationPreflightMissing(student) {
  const dobOk = isDobValidForArticulationNorms(student?.date_of_birth);
  const sexOk = isSexValidForArticulationNorms(student?.sex);
  return { dobMissing: !dobOk, sexMissing: !sexOk, ready: dobOk && sexOk };
}

export default function ArticulationScreenerPreflightModal({
  open,
  onOpenChange,
  student,
  onReady,
}) {
  const initial = useMemo(() => articulationPreflightMissing(student), [student]);
  const [dob, setDob] = useState("");
  const [sex, setSex] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Prefill any partial data the student already has so the
    // provider only edits the missing/unsupported fields.
    setDob(
      isDobValidForArticulationNorms(student?.date_of_birth)
        ? student.date_of_birth
        : "",
    );
    setSex(
      isSexValidForArticulationNorms(student?.sex)
        ? (student.sex || "").toLowerCase()
        : "",
    );
    setSaving(false);
  }, [open, student]);

  const dobValid = isDobValidForArticulationNorms(dob);
  const sexValid = isSexValidForArticulationNorms(sex);
  const canSave = dobValid && sexValid && !saving;

  const handleSave = async () => {
    if (!canSave || !student?.id) return;
    setSaving(true);
    const patch = {};
    // Only send fields that were originally missing so we don't
    // clobber other demographic edits. Also send fields the user
    // touched even if the initial value looked ok — defensive.
    if (initial.dobMissing || dob !== (student?.date_of_birth || "")) {
      patch.date_of_birth = dob;
    }
    if (
      initial.sexMissing ||
      sex !== (student?.sex || "").toLowerCase()
    ) {
      patch.sex = sex;
    }
    try {
      const resp = await api.updateStudentDemographics(student.id, patch);
      const updated = resp?.data || { ...student, ...patch };
      toast.success("Student information saved");
      onReady?.(updated);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not save student information";
      toast.error(typeof msg === "string" ? msg : "Could not save student information");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="articulation-preflight-modal"
        aria-describedby={undefined}
        className="w-[95vw] sm:max-w-[520px] bg-[#FBFAF6]"
      >
        <DialogHeader>
          <DialogTitle
            data-testid="articulation-preflight-title"
            className="text-[#1F2937] heading-font"
          >
            Student information needed before starting
          </DialogTitle>
        </DialogHeader>

        <div className="px-1 pb-1 space-y-4">
          <p
            data-testid="articulation-preflight-body"
            className="text-xs text-[#4B5563] leading-relaxed"
          >
            The Articulation Screener uses the student&apos;s Date of Birth and
            Sex to support accurate scoring and norm interpretation. Please
            complete the missing information before starting.
          </p>

          {initial.dobMissing && (
            <div data-testid="articulation-preflight-dob-field" className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wide text-[#6B7280]">
                Date of birth
              </Label>
              <Input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                data-testid="articulation-preflight-dob"
                className="h-9 text-sm"
                max={new Date().toISOString().slice(0, 10)}
              />
              {dob && !dobValid && (
                <p
                  data-testid="articulation-preflight-dob-error"
                  className="text-[11px] text-[#7A3A2C]"
                >
                  Enter a valid past date of birth.
                </p>
              )}
            </div>
          )}

          {initial.sexMissing && (
            <div data-testid="articulation-preflight-sex-field" className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wide text-[#6B7280]">
                Sex
              </Label>
              <select
                value={sex}
                onChange={(e) => setSex(e.target.value)}
                data-testid="articulation-preflight-sex"
                className="h-9 text-sm border border-[#d4d2cc] rounded-lg px-2 bg-white w-full"
              >
                <option value="">Select…</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
              <p className="text-[10px] text-[#6B7280] italic">
                Used for age-and-sex-based articulation norm interpretation.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            data-testid="articulation-preflight-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="articulation-preflight-save"
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
          >
            {saving ? "Saving…" : "Save and start screener"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
